import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../hooks/useOrganization';
import { formatCurrency } from '../../utils/currency';

const RequisitionForm = ({ onClose, onSuccess, editRequisition = null }) => {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [formData, setFormData] = useState({
    notes: '',
    priority: 'MEDIUM'
  });
  const [parts, setParts] = useState([]);
  const [items, setItems] = useState([{ partId: '', quantity: 1, unitPrice: 0, description: '', discount: 0 }]);
  const [loading, setLoading] = useState(false);
  const [vatRate, setVatRate] = useState(0);

  useEffect(() => {
    fetchParts();
    fetchVatRate();
    if (editRequisition) {
      populateForm();
    }
  }, [editRequisition]);

  const fetchVatRate = async () => {
    try {
      const response = await axios.get('/api/organization');
      const rate = parseFloat(response.data?.vatPercentage || response.data?.vat_rate || response.data?.vatRate || 0);
      setVatRate(rate);
    } catch (error) {
      setVatRate(0);
    }
  };

  const fetchParts = async () => {
    try {
      const response = await axios.get('/api/parts');
      setParts(response.data.parts || response.data || []);
    } catch (error) {
      toast.error('Failed to fetch parts');
    }
  };

  const populateForm = () => {
    setFormData({
      notes: editRequisition.notes || '',
      priority: editRequisition.priority || 'MEDIUM'
    });
    setItems(editRequisition.items || [{ partId: '', quantity: 1, unitPrice: 0, description: '', discount: 0 }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const subtotal = calculateSubtotal();
      const vatAmount = calculateVAT();
      const totalAmount = subtotal + vatAmount;
      
      const submitData = {
        ...formData,
        requesterId: user.id,
        items: items.filter(item => item.partId && item.quantity > 0),
        subtotal,
        vatRate,
        vatAmount,
        totalAmount
      };

      if (editRequisition) {
        await axios.put(`/api/requisitions/${editRequisition.id}`, submitData);
        toast.success('Requisition updated successfully');
      } else {
        await axios.post('/api/requisitions', submitData);
        toast.success('Requisition created successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save requisition');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, { partId: '', quantity: 1, unitPrice: 0, description: '', discount: 0 }]);
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    
    if (field === 'partId') {
      const part = parts.find(p => p.id === parseInt(value));
      if (part) {
        updated[index].unitPrice = parseFloat(part.price) || 0;
        updated[index].partName = part.name;
      }
    }
    
    setItems(updated);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => {
      const itemTotal = item.quantity * item.unitPrice;
      const discountAmount = itemTotal * ((item.discount || 0) / 100);
      return sum + (itemTotal - discountAmount);
    }, 0);
  };

  const calculateVAT = () => {
    const subtotal = calculateSubtotal();
    return subtotal * (vatRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateVAT();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {editRequisition ? 'Edit Requisition' : 'Create Purchase Requisition'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Requester</label>
              <input
                type="text"
                value={user?.name || ''}
                disabled
                className="w-full px-3 py-2 border rounded-md bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">Requested Items</label>
              <button
                type="button"
                onClick={addItem}
                className="flex items-center px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Item
              </button>
            </div>

            {items.map((item, index) => (
              <div key={index} className="mb-3 border-b pb-2">
                <div className="grid grid-cols-12 gap-2 mb-2">
                  <div className="col-span-4">
                    <select
                      value={item.partId}
                      onChange={(e) => updateItem(index, 'partId', e.target.value)}
                      className="w-full px-2 py-1 border rounded text-sm"
                    >
                      <option value="">Select Part</option>
                      {parts.map(part => (
                        <option key={part.id} value={part.id}>
                          {part.name} (Stock: {part.quantity})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border rounded text-sm"
                      min="1"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      placeholder="Unit Price"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                      step="0.01"
                      className="w-full px-2 py-1 border rounded text-sm"
                    />
                  </div>
                  <div className="col-span-2 relative">
                    <input
                      type="number"
                      placeholder="Discount"
                      value={item.discount}
                      onChange={(e) => updateItem(index, 'discount', parseFloat(e.target.value) || 0)}
                      step="0.01"
                      min="0"
                      max="100"
                      className="w-full px-2 py-1 pr-6 border rounded text-sm"
                    />
                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">%</span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-sm">{formatCurrency(item.quantity * item.unitPrice * (1 - (item.discount || 0) / 100), organization?.currency_code)}</span>
                  </div>
                  <div className="col-span-1">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-11">
                    <input
                      type="text"
                      placeholder="Additional description (optional)"
                      value={item.description || ''}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      className="w-full px-2 py-1 border rounded text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Additional notes or justification for this requisition..."
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span>Subtotal:</span>
              <span>{formatCurrency(calculateSubtotal(), organization?.currency_code)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span>VAT ({vatRate}%):</span>
              <span>{formatCurrency(calculateVAT(), organization?.currency_code)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t">
              <span className="text-lg font-semibold">Total:</span>
              <span className="text-lg font-bold">{formatCurrency(calculateTotal(), organization?.currency_code)}</span>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : editRequisition ? 'Update' : 'Submit Requisition'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequisitionForm;
